/**
 * Brand Kits API Service
 * Handles all brand-kit-related API calls.
 *
 * Base: /api/v1/ai/brand-kits  (role routing skipped – path is universal)
 *
 * Endpoints:
 *   GET    /ai/brand-kits            – List (no brand_data)
 *   GET    /ai/brand-kits/:id        – Show (full, incl. brand_data)
 *   POST   /ai/brand-kits            – Create
 *   PUT    /ai/brand-kits/:id        – Update
 *   DELETE /ai/brand-kits/:id        – Delete
 *   POST   /ai/brand-kits/extract    – Extract from file (multipart, async)
 */

import { apiClient } from './client'
import {
  BrandKit,
  BrandKitFormData,
  BrandKitStatus,
  formDataToBrandData,
} from '../../types/brandKit'

const BASE_PATH = '/ai/brand-kits'

export interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page?: number
}

interface BrandKitsListResponse {
  brand_kits: BrandKit[]
  meta: PaginationMeta
}

interface BrandKitResponse {
  brand_kit: BrandKit
}

interface ExtractBrandKitResponse {
  brand_kit: BrandKit
  message: string
}

interface DeleteBrandKitResponse {
  message: string
}

export interface BrandKitsQueryParams {
  page?: number
  per_page?: number
  q?: string
  advertiser_id?: string
  agency_id?: string
}

export interface ExtractBrandKitParams {
  file: File
  name?: string
  advertiser_ids?: string[]
  agency_ids?: string[]
}

function buildQueryString(params: BrandKitsQueryParams): string {
  const searchParams = new URLSearchParams()
  if (params.page && params.page > 1) searchParams.append('page', params.page.toString())
  if (params.per_page) searchParams.append('per_page', params.per_page.toString())
  if (params.q) searchParams.append('q', params.q)
  if (params.advertiser_id) searchParams.append('advertiser_id', params.advertiser_id)
  if (params.agency_id) searchParams.append('agency_id', params.agency_id)
  const qs = searchParams.toString()
  return qs ? `?${qs}` : ''
}

export const brandKitApi = {
  /**
   * List brand kits (paginated). Response omits brand_data for performance.
   */
  getBrandKits: async (
    params: BrandKitsQueryParams = {}
  ): Promise<{ brand_kits: BrandKit[]; meta: PaginationMeta }> => {
    const queryString = buildQueryString(params)
    const response = await apiClient.get<BrandKitsListResponse>(
      `${BASE_PATH}${queryString}`,
      { skipRoleRouting: true }
    )
    return {
      brand_kits: response.brand_kits || [],
      meta: response.meta || { current_page: 1, total_pages: 1, total_count: 0 },
    }
  },

  /**
   * Get a single brand kit with full brand_data.
   */
  getBrandKit: async (id: number): Promise<BrandKit> => {
    const response = await apiClient.get<BrandKitResponse>(
      `${BASE_PATH}/${id}`,
      { skipRoleRouting: true }
    )
    return response.brand_kit
  },

  /**
   * Create a brand kit manually from form data.
   */
  createBrandKit: async (data: BrandKitFormData): Promise<BrandKit> => {
    const response = await apiClient.post<BrandKitResponse>(
      BASE_PATH,
      {
        brand_kit: {
          name: data.name,
          status: 'active' as BrandKitStatus,
          advertiser_ids: (data.advertiser_ids || []).map(String),
          agency_ids: (data.agency_ids || []).map(String),
          brand_data: formDataToBrandData(data),
        },
      },
      { skipRoleRouting: true }
    )
    return response.brand_kit
  },

  /**
   * Update an existing brand kit. Only include fields to change.
   */
  updateBrandKit: async (
    id: number,
    data: Partial<BrandKitFormData>,
    extra?: { status?: BrandKitStatus }
  ): Promise<BrandKit> => {
    const payload: Record<string, any> = {}
    if (data.name !== undefined) payload.name = data.name
    if (extra?.status) payload.status = extra.status
    if (data.advertiser_ids !== undefined) payload.advertiser_ids = data.advertiser_ids.map(String)
    if (data.agency_ids !== undefined) payload.agency_ids = data.agency_ids?.map(String) || []

    const hasBrandFields = data.house || data.brands || data.contact
    if (hasBrandFields) {
      payload.brand_data = formDataToBrandData({
        name: data.name || '',
        house: data.house || { domain: '', name: '' },
        brands: data.brands || [],
        contact: data.contact,
        authorized_operators: data.authorized_operators,
        trademarks: data.trademarks,
      })
    }

    const response = await apiClient.put<BrandKitResponse>(
      `${BASE_PATH}/${id}`,
      { brand_kit: payload },
      { skipRoleRouting: true }
    )
    return response.brand_kit
  },

  /**
   * Delete a brand kit.
   */
  deleteBrandKit: async (id: number): Promise<string> => {
    const response = await apiClient.delete<DeleteBrandKitResponse>(
      `${BASE_PATH}/${id}`,
      { skipRoleRouting: true }
    )
    return response.message
  },

  /**
   * Extract brand data from a file (async).
   *
   * Returns a draft brand kit immediately (202 Accepted).
   * Subscribe to BrandKitChannel with the returned brand_kit.id
   * to receive extraction results via WebSocket.
   *
   * Accepted file types: PDF, DOC, DOCX, PPT, PPTX, TXT, CSV, PNG, JPEG, GIF, WEBP, SVG
   * Max file size: 25 MB
   */
  extractBrandKit: async (params: ExtractBrandKitParams): Promise<ExtractBrandKitResponse> => {
    const formData = new FormData()
    formData.append('file', params.file)
    if (params.name) formData.append('name', params.name)
    if (params.advertiser_ids?.length) {
      params.advertiser_ids.forEach(id => formData.append('advertiser_ids[]', id))
    }
    if (params.agency_ids?.length) {
      params.agency_ids.forEach(id => formData.append('agency_ids[]', id))
    }

    const response = await apiClient.postFormData<ExtractBrandKitResponse>(
      `${BASE_PATH}/extract`,
      formData,
      { skipRoleRouting: true }
    )
    return response
  },
}
